import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GST_modal from "../../../common/gst_modal/GST_modal";
import {
  faMobileAlt,
  faUser,
  faEnvelope,
  faBirthdayCake,
  faCheckCircle,
  faFileInvoiceDollar,
  faPercent,
} from "@fortawesome/free-solid-svg-icons";
import "./User.css";
import { Context } from "../../../../utils/context";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { getData, postData } from "../../../../utils/api";
import { getUserSearchSuggestions } from "../../../../utils/apis/orders/userSearch";

import SuggestionList from "../ProductLists/SuggestionList";

const CONTACT_DEBOUNCE_MS = 300;

function User({ setUserId, user_id, setTaxTypeId, GSTNumber, setGSTNumber }) {
  const [checkUser, setCheckUser] = useState(false);

  const [modalShow, setModalShow] = useState(false);
  const [taxTypes, setTaxTypes] = useState([]);

  // ---- Contact-number suggestion state ----
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactHighlightedIndex, setContactHighlightedIndex] = useState(-1);
  const [contactDropdownPos, setContactDropdownPos] = useState(null);
  const contactDebounceRef = useRef(null);
  const contactRequestIdRef = useRef(0);
  const contactWrapperRef = useRef(null);

  const skipContactResetRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: {
      tax_type_id: "2",
    },
  });

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    if (birthDate > today) return 0;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

 const onSubmit = async (data) => {
  const payload = { ...data, gst_number: GSTNumber };
  const res = await postData("/admin/offline-order/registerUser", payload);

  if (res.success) {
    alert("User registered successfully");
    const newUser = res?.data;

    setValue("user_id", newUser?.id);
    setUserId(newUser?.id);
    setValue("name", newUser?.name);
    setValue("email", newUser?.email);
    setValue("date_of_birth", newUser?.date_of_birth);
    setValue(
      "age",
      newUser?.date_of_birth ? calculateAge(newUser.date_of_birth) : "",
      { shouldDirty: true, shouldTouch: true, shouldValidate: true }
    );
    setGSTNumber(newUser?.gst_number || GSTNumber || "");
    setCheckUser(true);
  } else {
    if (res?.errors) {
      Object.keys(res?.errors).forEach((key) => {
        setError(key, { type: "manual", message: res?.errors[key] });
      });
    }
  }
};

  const getUserDetails = async () => {
    const contact_no = watch("contact_no");
    const name = watch("name");

    if (!contact_no || contact_no.length !== 10) {
      setError("contact_no", {
        type: "manual",
        message: "Enter valid 10-digit mobile number",
      });
      return;
    }

    if (!name || !name.trim()) {
      setError("name", {
        type: "manual",
        message: "Enter name to check user",
      });
      return;
    }

    const res = await getData(
      `/admin/offline-order/getUser?contact_no=${contact_no}&name=${encodeURIComponent(
        name
      )}`
    );

    if (res.success) {
      clearErrors("contact_no");
      clearErrors("name");
      setCheckUser(true);
      if (res?.data) {
        setValue("user_id", res?.data?.id);
        setUserId(res?.data?.id);
        setValue("name", res?.data?.name);
        setValue("email", res?.data?.email);
        setValue("date_of_birth", res?.data?.date_of_birth);
        setGSTNumber(res?.data?.gst_number || "");

        setValue(
          "age",
          res?.data?.date_of_birth
            ? calculateAge(res.data.date_of_birth)
            : ""
        );
      } else {
        setUserId(null);
        setValue("user_id", "");
        setValue("email", "");
        setValue("age", "");
        setGSTNumber("");
      }
    } else {
      setCheckUser(true);
      setError("contact_no", { type: "manual", message: "User Not Found" });
    }
  };

  const handleCheckUserEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!checkUser) {
        getUserDetails();
      }
    }
  };

  // ---- Contact-number suggestion helpers ----

  const fetchContactSuggestions = (kw) => {
    if (!kw || !kw.trim()) {
      setContactSuggestions([]);
      setContactLoading(false);
      return;
    }

    const reqId = contactRequestIdRef.current + 1;
    contactRequestIdRef.current = reqId;
    setContactLoading(true);

    getUserSearchSuggestions(kw)
      .then((list) => {
        if (contactRequestIdRef.current !== reqId) return;
        setContactSuggestions(list);
        setContactLoading(false);
        setContactHighlightedIndex(-1);
      })
      .catch(() => {
        if (contactRequestIdRef.current !== reqId) return;
        setContactSuggestions([]);
        setContactLoading(false);
      });
  };

  const handleContactSearchChange = (val) => {
    setShowContactDropdown(true);
    setContactHighlightedIndex(-1);

    if (contactDebounceRef.current) clearTimeout(contactDebounceRef.current);
    contactDebounceRef.current = setTimeout(() => {
      fetchContactSuggestions(val);
    }, CONTACT_DEBOUNCE_MS);
  };

  const handleSelectContactSuggestion = (user) => {
    skipContactResetRef.current = true;

    setShowContactDropdown(false);
    setContactSuggestions([]);
    setContactHighlightedIndex(-1);

    setValue("contact_no", user.contact_no || "");
    clearErrors("contact_no");
    setCheckUser(true);

    setValue("user_id", user.id);
    setUserId(user.id);
    setValue("name", user.name || "");
    setValue("email", user.email || "");
    setValue("date_of_birth", user.date_of_birth || "");
    setGSTNumber(user.gst_number || "");
    setValue(
      "age",
      user.date_of_birth ? calculateAge(user.date_of_birth) : "",
    );
  };

 
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        contactWrapperRef.current &&
        !contactWrapperRef.current.contains(e.target)
      ) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Fetch GST tax types once on mount for the "Select GST Type" dropdown.
  useEffect(() => {
    const fetchTaxTypes = async () => {
      try {
        const res = await getData("/admin/offline-order/getTaxTypes");
        if (res?.success) {
          setTaxTypes(res?.data || []);
        }
      } catch (err) {
        console.error("Error fetching tax types:", err);
      }
    };
    fetchTaxTypes();
  }, []);

  useEffect(() => {
    setTaxTypeId?.("2");
  }, []);

  useEffect(() => {
    if (skipContactResetRef.current) {
      skipContactResetRef.current = false;
      return;
    }
    setCheckUser(false);
    setValue("user_id", "");
    setUserId(null);
    setValue("name", "");
    setValue("email", "");
    setValue("age", "");
    setValue("date_of_birth", "");
    setValue("tax_type_id", "2");
    setTaxTypeId?.("2");
    setGSTNumber("");
  }, [watch("contact_no")]);

  const showGstStage = !(checkUser && !watch("user_id"));

  const contactValue = watch("contact_no") || "";
  const showContactPopup =
    showContactDropdown && contactValue.trim().length > 0;


  const contactSuggestionItems = contactSuggestions.map((u) => ({
    ...u,
    name: `${u.name || "Unknown"} — ${u.contact_no || ""}`,
  }));

  

  useEffect(() => {
    if (showContactPopup && contactWrapperRef.current) {
      const rect = contactWrapperRef.current.getBoundingClientRect();
      setContactDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setContactDropdownPos(null);
    }
  }, [showContactPopup, contactSuggestionItems.length, contactLoading]);

  return (
    <section className="user-container">
      <h3 className="user-title">Bapat Optics</h3>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="align-items-start user-single-row g-2 flex-nowrap overflow-auto">
          {/* Mobile */}
          <Col className="user-col">
            <Form.Group className="user-input-group">
              <div
                className="input-icon"
                style={{ position: "relative" }}
                ref={contactWrapperRef}
              >
                <FontAwesomeIcon icon={faMobileAlt} className="icon" />
                <Form.Control
                  size="sm"
                  type="tel"
                  placeholder="Mobile"
                  maxLength={10}
                  className="user-input small-input"
                  {...register("contact_no", {
                    required: "Mobile number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Enter valid 10-digit mobile number",
                    },
                    onChange: (e) => handleContactSearchChange(e.target.value),
                  })}
                  onFocus={() => {
                    if (contactValue.trim()) setShowContactDropdown(true);
                  }}
                  onKeyDown={(e) => {
                    if (showContactPopup && contactSuggestionItems.length) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setContactHighlightedIndex((i) =>
                          Math.min(i + 1, contactSuggestionItems.length - 1),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setContactHighlightedIndex((i) => Math.max(i - 1, 0));
                        return;
                      }
                      if (e.key === "Enter" && contactHighlightedIndex >= 0) {
                        e.preventDefault();
                        handleSelectContactSuggestion(
                          contactSuggestionItems[contactHighlightedIndex],
                        );
                        return;
                      }
                      if (e.key === "Escape") {
                        setShowContactDropdown(false);
                        return;
                      }
                    }

                    if (e.key === "Enter") {
                      handleCheckUserEnter(e);
                      return;
                    }
                    const allowedKeys = [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                    ];
                    if (
                      !(
                        (e.key >= "0" && e.key <= "9") ||
                        allowedKeys.includes(e.key)
                      )
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              {errors.contact_no && (
                <p className="text-danger small mb-0">
                  {errors.contact_no.message}
                </p>
              )}
            </Form.Group>
          </Col>

          {/* Full Name */}
          <Col className="user-col">
            <Form.Group className="user-input-group">
              <div className="input-icon">
                <FontAwesomeIcon icon={faUser} className="icon" />
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Full Name"
                  className="user-input"
                  {...register("name", { required: "Full name is required" })}
                  onKeyDown={handleCheckUserEnter}
                />
              </div>
              {errors.name && (
                <p className="text-danger small mb-0">{errors.name.message}</p>
              )}
            </Form.Group>
          </Col>

          {/* Email */}
          <Col className="user-col">
            <Form.Group className="user-input-group">
              <div className="input-icon">
                <FontAwesomeIcon icon={faEnvelope} className="icon" />
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="Email Address"
                  className="user-input"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Invalid email address",
                    },
                  })}
                  onKeyDown={handleCheckUserEnter}
                />
              </div>
              {errors.email && (
                <p className="text-danger small mb-0">{errors.email.message}</p>
              )}
            </Form.Group>
          </Col>

          {/* DOB (new user) or Age (existing user) */}
          {checkUser && !watch("user_id") ? (
            <Col className="user-col">
              <Form.Group className="user-input-group">
                <div className="input-icon">
                  <FontAwesomeIcon icon={faBirthdayCake} className="icon" />
                  <Form.Control
                    size="sm"
                    type="date"
                    placeholder="Date of Birth"
                    className="user-input"
                    max={new Date().toISOString().split("T")[0]}
                    {...register("date_of_birth", {
                      required: "Date of Birth is required",
                      validate: (value) => {
                        const today = new Date();
                        const dob = new Date(value);
                        if (dob >= today) {
                          return "Date of Birth must be in the past";
                        }
                        return true;
                      },
                    })}
                  />
                </div>
                {errors.date_of_birth && (
                  <p className="text-danger small mb-0">
                    {errors.date_of_birth.message}
                  </p>
                )}
              </Form.Group>
            </Col>
          ) : (
            <Col className="user-col age-col" xs="auto">
              <Form.Group className="user-input-group">
                <div className="input-icon">
                  <FontAwesomeIcon icon={faBirthdayCake} className="icon" />
                  <Form.Control
                    size="sm"
                    type="number"
                    placeholder="Age"
                    className="user-input age-input"
                    {...register("age", {
                      required: "Age is required",
                      min: { value: 1, message: "Age must be greater than 0" },
                      max: { value: 120, message: "Age must be less than 120" },
                    })}
                  />
                </div>
                {errors.age && (
                  <p className="text-danger small mb-0">{errors.age.message}</p>
                )}
              </Form.Group>
            </Col>
          )}

          {/* Refer Code (new user only) */}
          {checkUser && !watch("user_id") && (
            <Col className="user-col">
              <Form.Group className="user-input-group">
                <div className="input-icon">
                  <FontAwesomeIcon icon={faUser} className="icon" />
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Refer Code"
                    className="user-input"
                    {...register("refer_code")}
                  />
                </div>
              </Form.Group>
            </Col>
          )}

          {/* Select GST Type */}
          {showGstStage && (
            <Col className="user-col" xs="auto">
              <Form.Group className="user-input-group">
                <div className="input-icon">
                  <FontAwesomeIcon icon={faPercent} className="icon" />
                  <Form.Select
                    size="sm"
                    className="user-input"
                    {...register("tax_type_id", {
                      required: "GST type is required",
                      onChange: (e) => setTaxTypeId?.(e.target.value),
                    })}
                  >
                    <option value="">Select GST Type</option>
                    {taxTypes.map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {errors.tax_type_id && (
                  <p className="text-danger small mb-0">
                    {errors.tax_type_id.message}
                  </p>
                )}
              </Form.Group>
            </Col>
          )}

          {/* GST trigger */}
          <Col className="user-col" xs="auto">
            <Form.Group className="user-input-group">
              {checkUser && !watch("user_id") ? (
                <Button size="sm" className="user-btn" type="submit">
                  Register User
                </Button>
              ) : (
                <>
                  {GSTNumber ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{GSTNumber}</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="success"
                        className="user-btn d-flex align-items-center justify-content-center"
                        onClick={() => setModalShow(true)}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        GST Added
                      </Button>
                    </OverlayTrigger>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="user-btn d-flex align-items-center justify-content-center"
                      onClick={() => setModalShow(true)}
                    >
                      <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                      Add GST
                    </Button>
                  )}
                </>
              )}
            </Form.Group>
          </Col>

          {/* Check User */}
          <Col className="user-col" xs="auto">
            <Form.Group className="user-input-group">
              {!checkUser && (
                <Button size="sm" className="user-btn" onClick={getUserDetails}>
                  Check User
                </Button>
              )}
              {checkUser && watch("user_id") && (
                <div className="user-checked-msg text-success d-flex align-items-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  <span>User Checked</span>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {showContactPopup &&
        contactDropdownPos &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: contactDropdownPos.top,
              left: contactDropdownPos.left,
              minWidth: "260px",
              zIndex: 2000,
            }}
          >
            <SuggestionList
              suggestions={contactSuggestionItems}
              loading={contactLoading}
              keyword={contactValue}
              highlightedIndex={contactHighlightedIndex}
              onHover={setContactHighlightedIndex}
              onSelect={handleSelectContactSuggestion}
            />
          </div>,
          document.body
        )}

      <GST_modal
        GSTNumber={GSTNumber}
        setGSTNumber={setGSTNumber}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </section>
  );
}

export default User;